/**
 * WeaveIn.ai Platform - Health Plan Detail Page
 * Detailed view with tabs for LOBs, Products, Networks, Employer Contracts, CBO Partnerships
 */

import React, { useState, useEffect, useCallback } from 'react';
import { GatedTabContent, GatedTabTrigger } from '@/components/common/GatedTabContent';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2,
  ArrowLeft,
  Layers,
  Package,
  Network,
  FileText,
  Users,
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  DollarSign,
  Shield,
  Calendar,
  MapPin,
  Pill,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import PharmacyBenefitsManager from './PharmacyBenefitsManager';
import {
  healthPlansAPI,
  HealthPlan,
  LineOfBusiness,
  Product,
  Network as NetworkType,
  EmployerContract,
  CBOPartnership,
  Benefit,
  LOBCreate,
  ProductCreate,
  NetworkCreate,
  BenefitCreate,
  PLAN_TYPES,
  LOB_TYPES,
  PRODUCT_TYPES,
  METAL_TIERS,
  NETWORK_TYPES,
  CONTRACT_TYPES,
  BENEFIT_CATEGORIES,
  LOBType,
  ProductType,
  MetalTier,
  BenefitCategory,
} from '@/services/api/healthPlans';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// ============================================================================
// Tab Components
// ============================================================================

// LOBs Tab
interface LOBsTabProps {
  planId: number;
  lobs: LineOfBusiness[];
  onRefresh: () => void;
}

const LOBsTab: React.FC<LOBsTabProps> = ({ planId, lobs, onRefresh }) => {
  const { t } = useAppTranslation();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<LOBCreate>({
    lob_type: 'COMMERCIAL',
    name: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await healthPlansAPI.createLOB(planId, formData);
      setShowForm(false);
      setFormData({ lob_type: 'COMMERCIAL', name: '' });
      onRefresh();
    } catch (err) {
      console.error('Failed to create LOB:', err);
    } finally {
      setSaving(false);
    }
  };

  const getLOBTypeLabel = (type: LOBType) => LOB_TYPES.find(l => l.value === type)?.label || type;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('healthPlans.detail.linesOfBusiness', 'Lines of Business')} ({lobs.length})
        </h3>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> {t('healthPlans.detail.addLOB', 'Add LOB')}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              value={formData.lob_type}
              onChange={(e) => setFormData({ ...formData, lob_type: e.target.value as LOBType })}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
            >
              {LOB_TYPES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('healthPlans.detail.lobNamePlaceholder', 'LOB Name')}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
              required
            />
            <input
              type="text"
              value={formData.state || ''}
              onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase().slice(0, 2) })}
              placeholder={t('healthPlans.detail.stateOptional', 'State (optional)')}
              maxLength={2}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
              {saving ? t('healthPlans.common.creating', 'Creating...') : t('healthPlans.detail.createLOB', 'Create LOB')}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 text-gray-600 dark:text-gray-300">
              {t('healthPlans.common.cancel', 'Cancel')}
            </button>
          </div>
        </form>
      )}

      {lobs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">{t('healthPlans.detail.noLOBs', 'No lines of business defined')}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lobs.map(lob => (
            <div key={lob.id} className="p-4 bg-white dark:bg-gray-800 border rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">{lob.name}</h4>
                  <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                    {getLOBTypeLabel(lob.lob_type)}
                  </span>
                </div>
                {lob.state && (
                  <span className="text-sm text-gray-500">{lob.state}</span>
                )}
              </div>
              {lob.contract_number && (
                <p className="text-xs text-gray-500 mt-2">{t('healthPlans.detail.contract', 'Contract')}: {lob.contract_number}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Products Tab
interface ProductsTabProps {
  planId: number;
  lobs: LineOfBusiness[];
  onRefresh: () => void;
}

const ProductsTab: React.FC<ProductsTabProps> = ({ planId, lobs, onRefresh }) => {
  const { t } = useAppTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState<number | null>(null);
  const [formData, setFormData] = useState<ProductCreate>({
    lob_id: lobs[0]?.id || 0,
    product_code: '',
    name: '',
    product_type: 'PPO',
  });

  const loadProducts = useCallback(async () => {
    try {
      const data = await healthPlansAPI.listProducts(planId);
      setProducts(data);
    } catch (err) {
      console.error('Failed to load products:', err);
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await healthPlansAPI.createProduct(planId, formData);
      setShowForm(false);
      setFormData({ lob_id: lobs[0]?.id || 0, product_code: '', name: '', product_type: 'PPO' });
      loadProducts();
    } catch (err) {
      console.error('Failed to create product:', err);
    }
  };

  const loadProductDetails = async (productId: number) => {
    if (expandedProduct === productId) {
      setExpandedProduct(null);
      return;
    }
    try {
      const product = await healthPlansAPI.getProduct(planId, productId);
      setProducts(prev => prev.map(p => p.id === productId ? product : p));
      setExpandedProduct(productId);
    } catch (err) {
      console.error('Failed to load product details:', err);
    }
  };

  const formatCurrency = (amount?: number) => amount ? `$${amount.toLocaleString()}` : '-';

  if (loading) return <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('healthPlans.detail.products', 'Products')} ({products.length})</h3>
        <button
          onClick={() => setShowForm(true)}
          disabled={lobs.length === 0}
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" /> {t('healthPlans.detail.addProduct', 'Add Product')}
        </button>
      </div>

      {lobs.length === 0 && (
        <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300 rounded-lg text-sm">
          {t('healthPlans.detail.createLOBFirst', 'Create a Line of Business first before adding products')}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <select
              value={formData.lob_id}
              onChange={(e) => setFormData({ ...formData, lob_id: parseInt(e.target.value) })}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
              required
            >
              {lobs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <input
              type="text"
              value={formData.product_code}
              onChange={(e) => setFormData({ ...formData, product_code: e.target.value })}
              placeholder={t('healthPlans.detail.productCodePlaceholder', 'Product Code')}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
              required
            />
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('healthPlans.detail.productNamePlaceholder', 'Product Name')}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
              required
            />
            <select
              value={formData.product_type}
              onChange={(e) => setFormData({ ...formData, product_type: e.target.value as ProductType })}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
            >
              {PRODUCT_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <input
              type="number"
              value={formData.deductible_individual || ''}
              onChange={(e) => setFormData({ ...formData, deductible_individual: parseFloat(e.target.value) || undefined })}
              placeholder={t('healthPlans.form.individualDeductible', 'Individual Deductible')}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
            />
            <input
              type="number"
              value={formData.deductible_family || ''}
              onChange={(e) => setFormData({ ...formData, deductible_family: parseFloat(e.target.value) || undefined })}
              placeholder={t('healthPlans.form.familyDeductible', 'Family Deductible')}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
            />
            <input
              type="number"
              value={formData.out_of_pocket_max_individual || ''}
              onChange={(e) => setFormData({ ...formData, out_of_pocket_max_individual: parseFloat(e.target.value) || undefined })}
              placeholder={t('healthPlans.form.individualOOPMax', 'Individual OOP Max')}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
            />
            <input
              type="number"
              value={formData.out_of_pocket_max_family || ''}
              onChange={(e) => setFormData({ ...formData, out_of_pocket_max_family: parseFloat(e.target.value) || undefined })}
              placeholder={t('healthPlans.form.familyOOPMax', 'Family OOP Max')}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm">{t('healthPlans.detail.createProduct', 'Create Product')}</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 text-gray-600 dark:text-gray-300">{t('healthPlans.common.cancel', 'Cancel')}</button>
          </div>
        </form>
      )}

      {products.length === 0 ? (
        <div className="text-center py-8 text-gray-500">{t('healthPlans.detail.noProducts', 'No products defined')}</div>
      ) : (
        <div className="space-y-3">
          {products.map(product => (
            <div key={product.id} className="bg-white dark:bg-gray-800 border rounded-lg overflow-hidden">
              <div
                className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={() => loadProductDetails(product.id)}
              >
                <div className="flex items-center gap-3">
                  {expandedProduct === product.id ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">{product.name}</h4>
                    <p className="text-sm text-gray-500">{product.product_code} · {product.lob_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full">
                    {PRODUCT_TYPES.find(p => p.value === product.product_type)?.label}
                  </span>
                  {product.metal_tier && (
                    <span className="px-2 py-0.5 text-xs rounded-full" style={{
                      backgroundColor: METAL_TIERS.find(m => m.value === product.metal_tier)?.color + '20',
                      color: METAL_TIERS.find(m => m.value === product.metal_tier)?.color
                    }}>
                      {product.metal_tier}
                    </span>
                  )}
                </div>
              </div>

              {expandedProduct === product.id && (
                <div className="p-4 border-t bg-gray-50 dark:bg-gray-700/50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500">{t('healthPlans.planCard.individualDeductible', 'Individual Deductible')}</p>
                      <p className="font-medium">{formatCurrency(product.deductible_individual)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('healthPlans.planCard.familyDeductible', 'Family Deductible')}</p>
                      <p className="font-medium">{formatCurrency(product.deductible_family)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('healthPlans.planCard.individualOOPMax', 'Individual OOP Max')}</p>
                      <p className="font-medium">{formatCurrency(product.out_of_pocket_max_individual)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('healthPlans.planCard.familyOOPMax', 'Family OOP Max')}</p>
                      <p className="font-medium">{formatCurrency(product.out_of_pocket_max_family)}</p>
                    </div>
                  </div>

                  {product.benefits && product.benefits.length > 0 && (
                    <div>
                      <h5 className="font-medium text-sm mb-2">{t('healthPlans.planCard.benefits', 'Benefits')} ({product.benefits.length})</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {product.benefits.slice(0, 6).map(benefit => (
                          <div key={benefit.id} className="flex justify-between items-center p-2 bg-white dark:bg-gray-800 rounded text-sm">
                            <span>{benefit.benefit_name}</span>
                            <span className="text-gray-500">
                              {benefit.in_network_copay ? `$${benefit.in_network_copay} ${t('healthPlans.detail.copay', 'copay')}` :
                               benefit.in_network_coinsurance_pct ? `${benefit.in_network_coinsurance_pct}%` : t('healthPlans.benefitForm.covered', 'Covered')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Networks Tab
interface NetworksTabProps {
  planId: number;
  networks: NetworkType[];
  onRefresh: () => void;
}

const NetworksTab: React.FC<NetworksTabProps> = ({ planId, networks, onRefresh }) => {
  const { t } = useAppTranslation();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<NetworkCreate>({
    network_code: '',
    name: '',
    network_type: 'STANDARD',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await healthPlansAPI.createNetwork(planId, formData);
      setShowForm(false);
      setFormData({ network_code: '', name: '', network_type: 'STANDARD' });
      onRefresh();
    } catch (err) {
      console.error('Failed to create network:', err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('healthPlans.detail.providerNetworks', 'Provider Networks')} ({networks.length})</h3>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> {t('healthPlans.detail.addNetwork', 'Add Network')}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              value={formData.network_code}
              onChange={(e) => setFormData({ ...formData, network_code: e.target.value })}
              placeholder={t('healthPlans.detail.networkCodePlaceholder', 'Network Code')}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
              required
            />
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('healthPlans.detail.networkNamePlaceholder', 'Network Name')}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
              required
            />
            <select
              value={formData.network_type}
              onChange={(e) => setFormData({ ...formData, network_type: e.target.value })}
              className="px-3 py-2 border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white"
            >
              {NETWORK_TYPES.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm">{t('healthPlans.detail.createNetwork', 'Create Network')}</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 text-gray-600 dark:text-gray-300">{t('healthPlans.common.cancel', 'Cancel')}</button>
          </div>
        </form>
      )}

      {networks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">{t('healthPlans.detail.noNetworks', 'No provider networks defined')}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {networks.map(network => (
            <div key={network.id} className="p-4 bg-white dark:bg-gray-800 border rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-medium text-gray-900 dark:text-white">{network.name}</h4>
                <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
                  {NETWORK_TYPES.find(n => n.value === network.network_type)?.label}
                </span>
              </div>
              <p className="text-sm text-gray-500 mb-2">{t('healthPlans.detail.code', 'Code')}: {network.network_code}</p>
              {network.geographic_scope && (
                <p className="text-xs text-gray-500">{t('healthPlans.detail.scope', 'Scope')}: {network.geographic_scope}</p>
              )}
              <div className="mt-3 flex gap-4 text-xs text-gray-500">
                {network.provider_count && <span>{network.provider_count.toLocaleString()} {t('healthPlans.detail.providers', 'Providers')}</span>}
                {network.facility_count && <span>{network.facility_count.toLocaleString()} {t('healthPlans.detail.facilities', 'Facilities')}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Employer Contracts Tab
interface EmployerContractsTabProps {
  planId: number;
}

const EmployerContractsTab: React.FC<EmployerContractsTabProps> = ({ planId }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [contracts, setContracts] = useState<EmployerContract[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await healthPlansAPI.listEmployerContracts(planId);
        setContracts(data);
      } catch (err) {
        console.error('Failed to load employer contracts:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [planId]);

  if (loading) return <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('healthPlans.detail.employerContracts', 'Employer Contracts')} ({contracts.length})</h3>
      </div>

      {contracts.length === 0 ? (
        <div className="text-center py-8 text-gray-500">{t('healthPlans.detail.noEmployerContracts', 'No employer contracts')}</div>
      ) : (
        <div className="space-y-3">
          {contracts.map(contract => (
            <div key={contract.id} className="p-4 bg-white dark:bg-gray-800 border rounded-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">{contract.employer_name}</h4>
                  <p className="text-sm text-gray-500">
                    {contract.contract_number && `${t('healthPlans.detail.contract', 'Contract')}: ${contract.contract_number}`}
                    {contract.group_number && ` · ${t('healthPlans.detail.group', 'Group')}: ${contract.group_number}`}
                  </p>
                </div>
                <span className={`px-2 py-0.5 text-xs rounded-full ${
                  contract.contract_type === 'SELF_INSURED' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                  contract.contract_type === 'FULLY_INSURED' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                  {CONTRACT_TYPES.find(c => c.value === contract.contract_type)?.label}
                </span>
              </div>
              <div className="mt-2 flex gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {fmt.short(contract.effective_date)}
                  {contract.termination_date && ` - ${fmt.short(contract.termination_date)}`}
                </span>
              </div>
              {contract.contract_type === 'SELF_INSURED' && contract.stop_loss_carrier && (
                <div className="mt-2 text-xs text-gray-500">
                  <span className="font-medium">{t('healthPlans.detail.stopLoss', 'Stop Loss')}:</span> {contract.stop_loss_carrier}
                  {contract.specific_deductible && ` · ${t('healthPlans.detail.specific', 'Specific')}: $${contract.specific_deductible.toLocaleString()}`}
                  {contract.aggregate_deductible && ` · ${t('healthPlans.detail.aggregate', 'Aggregate')}: $${contract.aggregate_deductible.toLocaleString()}`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// CBO Partnerships Tab
interface CBOPartnershipsTabProps {
  planId: number;
}

const CBOPartnershipsTab: React.FC<CBOPartnershipsTabProps> = ({ planId }) => {
  const { t } = useAppTranslation();
  const [partnerships, setPartnerships] = useState<CBOPartnership[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await healthPlansAPI.listCBOPartnerships(planId);
        setPartnerships(data);
      } catch (err) {
        console.error('Failed to load CBO partnerships:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [planId]);

  if (loading) return <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div></div>;

  const getPartnershipTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      NETWORK_PARTNER: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      VALUE_BASED_CARE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      CARE_COORDINATION: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      SOCIAL_DETERMINANTS: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      NAVIGATION: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200',
    };
    return colors[type] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('healthPlans.detail.cboPartnerships', 'CBO Partnerships')} ({partnerships.length})</h3>
      </div>

      {partnerships.length === 0 ? (
        <div className="text-center py-8 text-gray-500">{t('healthPlans.detail.noCBOPartnerships', 'No CBO partnerships')}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {partnerships.map(partnership => (
            <div key={partnership.id} className="p-4 bg-white dark:bg-gray-800 border rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h4 className="font-medium text-gray-900 dark:text-white">{partnership.cbo_name}</h4>
                <span className={`px-2 py-0.5 text-xs rounded-full ${getPartnershipTypeColor(partnership.partnership_type)}`}>
                  {partnership.partnership_type.replace(/_/g, ' ')}
                </span>
              </div>
              {partnership.lob_name && (
                <p className="text-sm text-gray-500 mb-2">{t('healthPlans.detail.lob', 'LOB')}: {partnership.lob_name}</p>
              )}
              {partnership.services_provided && partnership.services_provided.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {partnership.services_provided.map((service, i) => (
                    <span key={i} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                      {service}
                    </span>
                  ))}
                </div>
              )}
              {partnership.reimbursement_model && (
                <p className="text-xs text-gray-500 mt-2">
                  {t('healthPlans.detail.reimbursement', 'Reimbursement')}: {partnership.reimbursement_model.replace(/_/g, ' ')}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

interface HealthPlanDetailProps {
  planId?: number;
  onBack?: () => void;
}

export default function HealthPlanDetail({ planId: propPlanId, onBack }: HealthPlanDetailProps) {
  const { t } = useAppTranslation();
  const { planId: routePlanId } = useParams<{ planId: string }>();
  const navigate = useNavigate();

  // Use prop planId if provided, otherwise fall back to route param
  const planId = propPlanId || (routePlanId ? parseInt(routePlanId) : undefined);

  const [plan, setPlan] = useState<HealthPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'lobs' | 'products' | 'networks' | 'employers' | 'cbos' | 'pharmacy'>('lobs');

  const loadPlan = useCallback(async () => {
    if (!planId) return;
    try {
      setLoading(true);
      const data = await healthPlansAPI.getHealthPlan(planId);
      setPlan(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load health plan');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/admin/health-plans');
    }
  };

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-red-600 mb-4">{error || t('healthPlans.errors.planNotFound', 'Health plan not found')}</p>
        <button onClick={handleBack} className="text-blue-600 hover:underline">
          {t('healthPlans.detail.backToHealthPlans', 'Back to Health Plans')}
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'lobs', label: t('healthPlans.tabs.linesOfBusiness', 'Lines of Business'), icon: Layers, count: plan.lines_of_business?.length || 0 },
    { id: 'products', label: t('healthPlans.tabs.products', 'Products'), icon: Package },
    { id: 'networks', label: t('healthPlans.tabs.networks', 'Networks'), icon: Network, count: plan.networks?.length || 0 },
    { id: 'pharmacy', label: t('healthPlans.tabs.pharmacyBenefits', 'Pharmacy Benefits'), icon: Pill },
    { id: 'employers', label: t('healthPlans.tabs.employerContracts', 'Employer Contracts'), icon: FileText },
    { id: 'cbos', label: t('healthPlans.tabs.cboPartnerships', 'CBO Partnerships'), icon: Users },
  ];

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{plan.name}</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {plan.short_name && `${plan.short_name} · `}
              {PLAN_TYPES.find(p => p.value === plan.plan_type)?.label}
              {plan.electronic_payer_id && ` · Payer ID: ${plan.electronic_payer_id}`}
            </p>
          </div>
        </div>

        {/* Plan Info Summary */}
        <div className="flex flex-wrap gap-6 text-sm text-gray-600 dark:text-gray-400 mb-4">
          {plan.headquarters_city && plan.headquarters_state && (
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {plan.headquarters_city}, {plan.headquarters_state}
            </span>
          )}
          {plan.main_phone && (
            <span>{plan.main_phone}</span>
          )}
          {plan.website && (
            <a href={plan.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              {plan.website.replace(/^https?:\/\//, '')}
            </a>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 -mb-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count !== undefined && (
                <span className="px-1.5 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 rounded-full">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto p-6">
        {activeTab === 'lobs' && (
          <GatedTabContent pageKey="health-plan-detail" tabKey="lobs">
            <LOBsTab planId={plan.id} lobs={plan.lines_of_business || []} onRefresh={loadPlan} />
          </GatedTabContent>
        )}
        {activeTab === 'products' && (
          <GatedTabContent pageKey="health-plan-detail" tabKey="products">
            <ProductsTab planId={plan.id} lobs={plan.lines_of_business || []} onRefresh={loadPlan} />
          </GatedTabContent>
        )}
        {activeTab === 'networks' && (
          <GatedTabContent pageKey="health-plan-detail" tabKey="networks">
            <NetworksTab planId={plan.id} networks={plan.networks || []} onRefresh={loadPlan} />
          </GatedTabContent>
        )}
        {activeTab === 'pharmacy' && (
          <GatedTabContent pageKey="health-plan-detail" tabKey="pharmacy">
            <PharmacyBenefitsManager planId={plan.id} isReadOnly={false} />
          </GatedTabContent>
        )}
        {activeTab === 'employers' && (
          <GatedTabContent pageKey="health-plan-detail" tabKey="employers">
            <EmployerContractsTab planId={plan.id} />
          </GatedTabContent>
        )}
        {activeTab === 'cbos' && (
          <GatedTabContent pageKey="health-plan-detail" tabKey="cbos">
            <CBOPartnershipsTab planId={plan.id} />
          </GatedTabContent>
        )}
      </div>
    </div>
  );
}
